import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { retreatService } from '../../services/retreatService';
import {
  CreateRetreatRequest,
  BasicInformationStepData,
  RetreatDetailsStepData,
  PriceDatesStepData,
  AccommodationStepData,
  MediaStepData,
  TeachersStepData,
  ApiResponse
} from '../../types';

interface CreateRetreatState {
  currentStep: string;
  formData: {
    basicInfo: Partial<BasicInformationStepData>;
    retreatDetails: Partial<RetreatDetailsStepData>;
    priceDates: Partial<PriceDatesStepData>;
    accommodations: Partial<AccommodationStepData>;
    media: Partial<MediaStepData>;
    teachers: Partial<TeachersStepData>;
    programFood: any;
    localInfo: any;
    travelGuide: any;
  };
  loading: boolean;
  error: string | null;
  isDraft: boolean;
  retreatId: string | null;
  validationErrors: Record<string, string[]>;
  relatedEntities: {
    teachers: any[];
    organizers: any[];
    centers: any[];
    accommodations: {
      data: any[];
      pagination?: {
        page: number;
        limit: number;
        total: number;
        pages: number;
      };
    };
    loading: boolean;
  };
}

const initialState: CreateRetreatState = {
  currentStep: 'basic',
  formData: {
    basicInfo: {},
    retreatDetails: {},
    priceDates: {},
    accommodations: {},
    media: {},
    teachers: {},
    programFood: {},
    localInfo: {},
    travelGuide: {},
  },
  loading: false,
  error: null,
  isDraft: false,
  retreatId: null,
  validationErrors: {},
  relatedEntities: {
    teachers: [],
    organizers: [],
    centers: [],
    accommodations: { data: [] },
    loading: false,
  },
};

// Async thunks
export const createRetreat = createAsyncThunk(
  'createRetreat/create',
  async (retreatData: CreateRetreatRequest, { rejectWithValue }) => {
    try {
      const response = await retreatService.createRetreat(retreatData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create retreat');
    }
  }
);

export const saveDraft = createAsyncThunk(
  'createRetreat/saveDraft',
  async (retreatData: CreateRetreatRequest, { rejectWithValue }) => {
    try {
      const response = await retreatService.saveDraft(retreatData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to save draft');
    }
  }
);

export const updateRetreat = createAsyncThunk(
  'createRetreat/update',
  async ({ id, data }: { id: string; data: Partial<CreateRetreatRequest> }, { rejectWithValue }) => {
    try {
      const response = await retreatService.updateRetreat(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update retreat');
    }
  }
);

// Related entities thunks
export const fetchTeachers = createAsyncThunk(
  'createRetreat/fetchTeachers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await retreatService.getTeachers();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch teachers');
    }
  }
);

export const createTeacher = createAsyncThunk(
  'createRetreat/createTeacher',
  async (teacherData: any, { rejectWithValue }) => {
    try {
      const response = await retreatService.createTeacher(teacherData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create teacher');
    }
  }
);

export const fetchOrganizers = createAsyncThunk(
  'createRetreat/fetchOrganizers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await retreatService.getOrganizers();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch organizers');
    }
  }
);

export const createOrganizer = createAsyncThunk(
  'createRetreat/createOrganizer',
  async (organizerData: any, { rejectWithValue }) => {
    try {
      const response = await retreatService.createOrganizer(organizerData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create organizer');
    }
  }
);

export const fetchCenters = createAsyncThunk(
  'createRetreat/fetchCenters',
  async (_, { rejectWithValue }) => {
    try {
      const response = await retreatService.getCenters();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch centers');
    }
  }
);

export const createCenter = createAsyncThunk(
  'createRetreat/createCenter',
  async (centerData: any, { rejectWithValue }) => {
    try {
      const response = await retreatService.createCenter(centerData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create center');
    }
  }
);

export const fetchAccommodations = createAsyncThunk(
  'createRetreat/fetchAccommodations',
  async (params: { page?: number; limit?: number } = {}, { rejectWithValue }) => {
    try {
      const response = await retreatService.getAccommodations(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch accommodations');
    }
  }
);

export const createAccommodation = createAsyncThunk(
  'createRetreat/createAccommodation',
  async (accommodationData: any, { rejectWithValue }) => {
    try {
      const response = await retreatService.createAccommodation(accommodationData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create accommodation');
    }
  }
);

const createRetreatSlice = createSlice({
  name: 'createRetreat',
  initialState,
  reducers: {
    setCurrentStep: (state, action: PayloadAction<string>) => {
      state.currentStep = action.payload;
    },
    updateStepData: (state, action: PayloadAction<{ step: string; data: any }>) => {
      const { step, data } = action.payload;
      state.formData[step as keyof typeof state.formData] = {
        ...state.formData[step as keyof typeof state.formData],
        ...data,
      };
    },
    clearFormData: (state) => {
      state.formData = initialState.formData;
      state.currentStep = 'basic';
      state.isDraft = false;
      state.retreatId = null;
      state.validationErrors = {};
      state.error = null;
    },
    setValidationErrors: (state, action: PayloadAction<Record<string, string[]>>) => {
      state.validationErrors = action.payload;
    },
    clearValidationErrors: (state) => {
      state.validationErrors = {};
    },
    setIsDraft: (state, action: PayloadAction<boolean>) => {
      state.isDraft = action.payload;
    },
    setRetreatId: (state, action: PayloadAction<string | null>) => {
      state.retreatId = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Create retreat
    builder
      .addCase(createRetreat.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRetreat.fulfilled, (state, action) => {
        state.loading = false;
        state.retreatId = action.payload.data?.id || null;
        state.isDraft = false;
      })
      .addCase(createRetreat.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Save draft
      .addCase(saveDraft.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveDraft.fulfilled, (state, action) => {
        state.loading = false;
        state.retreatId = action.payload.data?.id || null;
        state.isDraft = true;
      })
      .addCase(saveDraft.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update retreat
      .addCase(updateRetreat.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRetreat.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateRetreat.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Related entities
      .addCase(fetchTeachers.pending, (state) => {
        state.relatedEntities.loading = true;
      })
      .addCase(fetchTeachers.fulfilled, (state, action) => {
        state.relatedEntities.loading = false;
        state.relatedEntities.teachers = action.payload.data;
      })
      .addCase(fetchTeachers.rejected, (state) => {
        state.relatedEntities.loading = false;
      })

      .addCase(createTeacher.fulfilled, (state, action) => {
        state.relatedEntities.teachers.push(action.payload);
      })

      .addCase(fetchOrganizers.fulfilled, (state, action) => {
        state.relatedEntities.organizers = action.payload.data;
      })

      .addCase(createOrganizer.fulfilled, (state, action) => {
        state.relatedEntities.organizers.push(action.payload);
      })

      .addCase(fetchCenters.fulfilled, (state, action) => {
        state.relatedEntities.centers = action.payload.data;
      })

      .addCase(createCenter.fulfilled, (state, action) => {
        state.relatedEntities.centers.push(action.payload);
      })

      .addCase(fetchAccommodations.fulfilled, (state, action) => {
        // The API response structure is: { statusCode, message, data: { data: [...], pagination: {...} } }
        const responseData = action.payload.data || action.payload;
        state.relatedEntities.accommodations = {
          data: responseData.data || responseData,
          pagination: responseData.pagination
        };
      })

      .addCase(createAccommodation.fulfilled, (state, action) => {
        state.relatedEntities.accommodations.data.push(action.payload);
      });
  },
});

export const {
  setCurrentStep,
  updateStepData,
  clearFormData,
  setValidationErrors,
  clearValidationErrors,
  setIsDraft,
  setRetreatId,
  clearError,
} = createRetreatSlice.actions;

export default createRetreatSlice.reducer;
